<!-- Scheduled Orders Table with Row Expansion -->
<div
  class="scheduled-orders-table"
  [class.optimized-layout]="optimizedLayout"
  *ngIf="data && data.Items && data.Items.length > 0"
>
  <!-- Main Data Table with Row Expansion -->
  <p-table
    #scheduledOrdersTable
    [value]="data.Items || []"
    [loading]="loading"
    [paginator]="true"
    [rows]="data.PageSize || 25"
    [totalRecords]="data.TotalRecords || 0"
    [lazy]="true"
    [sortMode]="'single'"
    [showCurrentPageReport]="true"
    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
    [rowsPerPageOptions]="pageSizeOptions"
    [virtualScroll]="virtualScrolling"
    [scrollHeight]="scrollHeight"
    [scrollable]="true"
    scrollDirection="both"
    size="small"
    [responsiveLayout]="responsiveLayout"
    [breakpoint]="breakpoint"
    dataKey="Id"
    [rowExpandMode]="'single'"
    [expandedRowKeys]="expandedRows"
    [(selection)]="selectedItems"
    (selectionChange)="onSelectionChange()"
    (onSort)="onSort($event)"
    (onPage)="onPageChange($event)"
    (onRowDblClick)="onRowDoubleClick($event)"
    (onRowExpand)="onRowExpand($event)"
    (onRowCollapse)="onRowCollapse($event)"
  >
    <!-- Table Header -->
    <ng-template pTemplate="header">
      <tr>
        <!-- Expand/Collapse Column (Frozen Left) -->
        <th
          style="width: 2.5rem"
          class="text-center"
          pFrozenColumn
          [frozen]="true"
          alignFrozen="left"
        >
          <app-bulk-actions-toolbar
            [selectedCount]="selectedItems.length"
            [loading]="bulkLoading"
            (bulkAction)="bulkAction.emit($event)"
          />
        </th>

        <!-- Bulk Selection Checkbox Column (Frozen Left) -->
        <th
          *ngIf="showCheckboxes"
          style="width: 2.5rem"
          pFrozenColumn
          [frozen]="true"
          alignFrozen="left"
        >
          <p-checkbox
            [binary]="true"
            [ngModel]="isAllSelected()"
            [indeterminate]="isSomeSelected()"
            (ngModelChange)="toggleSelectAll()"
            [disabled]="loading || !data?.Items?.length"
          />
        </th>

        <!-- ID Column (Frozen Left) -->
        <th
          pFrozenColumn
          [frozen]="true"
          alignFrozen="left"
          [pSortableColumn]="'Id'"
          class="sortable-column id-column-header"
          style="width: auto; min-width: 4rem"
        >
          ID
          <p-sortIcon field="Id" />
        </th>

        <!-- Primary Columns for Main Row (excluding ID) -->
        <th
          *ngFor="
            let col of getPrimaryColumns().slice(1);
            trackBy: trackByColumn
          "
          [style.width]="col.width"
          [style.min-width]="col.minWidth"
          [pSortableColumn]="col.sortable ? col.field : undefined"
          class="sortable-column"
          [ngClass]="{
            'auto-width': col.width === 'auto',
            'fixed-width': col.width !== 'auto',
          }"
        >
          {{ col.header }}
          <p-sortIcon *ngIf="col.sortable" [field]="col.field" />
        </th>

        <!-- Actions Column (Frozen Right) -->
        <th
          *ngIf="showActions"
          style="width: 3rem"
          pFrozenColumn
          [frozen]="true"
          alignFrozen="right"
        >
          Actions
        </th>
      </tr>
    </ng-template>

    <!-- Table Body - Desktop View -->
    <ng-template
      pTemplate="body"
      let-item
      let-rowIndex="rowIndex"
      let-expanded="expanded"
    >
      <!-- Desktop Table Row - Clickable to expand/collapse -->
      <tr
        class="desktop-row"
        [ngClass]="{ 'selected-row': selectedItems.includes(item) }"
        (click)="toggleRowExpansion(item)"
      >
        <!-- Row Expansion Toggle (Frozen Left) -->
        <td
          class="text-center expand-column"
          pFrozenColumn
          [frozen]="true"
          alignFrozen="left"
          (click)="$event.stopPropagation()"
        >
          <span class="p-column-title">Expand</span>
          <p-button
            [pRowToggler]="item"
            type="button"
            pRipple
            [text]="true"
            [rounded]="true"
            [plain]="true"
            size="small"
            styleClass="text-gray-400 hover:text-gray-600"
            [icon]="expanded ? 'pi pi-chevron-down' : 'pi pi-chevron-right'"
            [disabled]="loading"
            pTooltip="Click to expand/collapse row details"
            tooltipPosition="top"
          />
        </td>

        <!-- Row Selection Checkbox (Frozen Left) -->
        <td
          *ngIf="showCheckboxes"
          class="checkbox-column"
          pFrozenColumn
          [frozen]="true"
          alignFrozen="left"
          (click)="$event.stopPropagation()"
        >
          <span class="p-column-title">Select</span>
          <p-checkbox
            [value]="item"
            [(ngModel)]="selectedItems"
            [binary]="false"
            (onChange)="onSelectionChange()"
            [disabled]="loading"
          />
        </td>

        <!-- ID Column (Frozen Left) - Click opens edit -->
        <td class="id-column" pFrozenColumn [frozen]="true" alignFrozen="left">
          <span class="p-column-title">ID</span>
          <a
            href="javascript:;"
            class="id-link"
            (click)="
              rowAction.emit({ action: 'edit', item }); $event.stopPropagation()
            "
          >
            #{{ item?.Id }}
          </a>
        </td>

        <!-- Status Column with Processing Indicators -->
        <td>
          <span class="p-column-title">Status</span>
          <div class="flex align-items-start gap-1">
            <!-- Processing Status with Spinner -->
            <div
              *ngIf="
                shouldShowProcessingSpinner(item?.Status);
                else normalStatus
              "
              class="flex align-items-center gap-1"
            >
              <p-tag value="PROCESSING" severity="info" [rounded]="true" />
              <p-progressSpinner
                [style]="{ width: '14px', height: '14px' }"
                strokeWidth="4"
                pTooltip="This Scheduled Order is Processing"
                tooltipPosition="top"
              />
            </div>

            <!-- Regular Status Display (includes Queued) -->
            <ng-template #normalStatus>
              <p-tag
                [value]="item?.Status"
                [severity]="getStatusSeverity(item?.Status)"
                [rounded]="true"
                [styleClass]="'p-tag-' + getStatusSeverity(item?.Status)"
              />
            </ng-template>

            <!-- Queued Status Timer Icon -->
            <i
              *ngIf="shouldShowQueuedIcon(item?.Status)"
              class="pi pi-clock text-blue-500"
              style="font-size: 0.75rem"
              pTooltip="This Scheduled Order is Queued for Processing"
              tooltipPosition="top"
            ></i>

            <!-- Processing Error Code -->
            <i
              *ngIf="
                item?.Status === 'Failed' && item?.ProcessingErrorCode !== null
              "
              class="pi pi-exclamation-triangle text-red-500"
              style="font-size: 0.75rem"
              [pTooltip]="
                getProcessingErrorDescription(item?.ProcessingErrorCode)
              "
              tooltipPosition="top"
            ></i>
          </div>
        </td>

        <!-- Validation Column -->
        <td class="validation-cell" (click)="$event.stopPropagation()">
          <span class="p-column-title">Validation</span>
          <ng-container *ngIf="getValidationChecksDisplay(item) as validation">
            <i
              [class]="validation.icon"
              [ngClass]="{
                'text-green-500': validation.severity === 'success',
                'text-yellow-500': validation.severity === 'warning',
                'text-red-500': validation.severity === 'danger',
              }"
              style="font-size: 1rem"
              [pTooltip]="validation.tooltip + ' Click for details.'"
              tooltipPosition="top"
              [style.cursor]="'pointer'"
              (click)="openValidationDialog(item)"
            ></i>
          </ng-container>
        </td>

        <!-- Indicators Column (Dunning, Locked) -->
        <td>
          <span class="p-column-title">Indicators</span>
          <div class="flex align-items-center gap-1">
            <!-- Dunning Indicator -->
            <i
              *ngIf="shouldShowDunningIndicator(item)"
              class="pi pi-dollar text-orange-500"
              style="font-size: 1rem"
              pTooltip="Customer is in dunning process"
              tooltipPosition="top"
            ></i>

            <!-- Locked Indicator -->
            <i
              *ngIf="shouldShowLockedIndicator(item)"
              class="pi pi-lock text-gray-500"
              style="font-size: 1rem"
              pTooltip="Scheduled order is locked"
              tooltipPosition="top"
            ></i>
          </div>
        </td>

        <!-- Customer Name Column -->
        <td>
          <span class="p-column-title">Customer</span>
          <span class="font-medium">{{ getCustomerFullName(item) }}</span>
        </td>

        <!-- Customer Email Column -->
        <td>
          <span class="p-column-title">Customer Email</span>
          <a
            [href]="'mailto:' + item?.CustomerEmail"
            class="text-sm text-primary-500 hover:text-primary-600 no-underline"
          >
            {{ item?.CustomerEmail }}
          </a>
        </td>

        <!-- Last Cycle Status -->
        <td (click)="$event.stopPropagation()">
          <span class="p-column-title">Last Cycle Status</span>
          <div *ngIf="item.LastProcessingCycle; else noLastCycle">
            <a
              href="javascript:;"
              class="text-primary-500 hover:text-primary-600 no-underline"
              [pTooltip]="'View processing logs for this cycle'"
              tooltipPosition="top"
              (click)="openProcessingLogsDialog(item)"
            >
              {{ getLastProcessingCycleStatus(item) }}
            </a>
          </div>
          <ng-template #noLastCycle>
            <span class="text-gray-500">-</span>
          </ng-template>
        </td>

        <!-- Last Processed -->
        <td>
          <span class="p-column-title">Last Processed</span>
          <span *ngIf="item?.LastOccurrenceUtc && site">{{
            formatDateWithSiteTimezone(item?.LastOccurrenceUtc, site?.UtcOffset)
              | date: "MMM d yyyy"
          }}</span>
          <span *ngIf="!item?.LastOccurrenceUtc" class="text-gray-500">-</span>
        </td>

        <!-- Next Occurrence -->
        <td (click)="$event.stopPropagation()">
          <span class="p-column-title">Next Occurrence</span>
          <span
            *ngIf="item?.NextOccurrenceUtc && site"
            class="cursor-pointer"
            (click)="showTimezoneDetails($event, item, 'NextOccurrenceUtc')"
            [pTooltip]="
              'Site timezone: ' +
              getFormattedGmt(site.UtcOffset) +
              '. Click for details.'
            "
            tooltipPosition="top"
            >{{
              formatDateWithSiteTimezone(
                item?.NextOccurrenceUtc,
                site?.UtcOffset
              ) | date: "MMM d yyyy"
            }}</span
          >
        </td>

        <!-- Billing Cycle -->
        <td>
          <span class="p-column-title">Billing Cycle</span>
          {{ item?.Cycles || 0 }}
        </td>

        <!-- Frequency -->
        <td>
          <span class="p-column-title">Frequency</span>
          <span class="whitespace-nowrap">{{
            formatFrequencyCompact(item?.Frequency, item?.FrequencyType)
          }}</span>
        </td>

        <!-- Row Actions Menu (Frozen Right) -->
        <td
          *ngIf="showActions"
          class="actions-column"
          pFrozenColumn
          [frozen]="true"
          alignFrozen="right"
          (click)="$event.stopPropagation()"
        >
          <span class="p-column-title">Actions</span>
          <p-button
            icon="pi pi-ellipsis-v"
            [text]="true"
            size="small"
            [disabled]="loading"
            (click)="toggleRowMenu($event, item)"
          />
        </td>
      </tr>

      <!-- Mobile Card Row -->
      <tr
        class="mobile-card-row"
        [ngClass]="{ 'selected-row': selectedItems.includes(item) }"
      >
        <td
          [attr.colspan]="
            getPrimaryColumns().length +
            (showCheckboxes ? 1 : 0) +
            (showActions ? 1 : 0) +
            1
          "
        >
          <!-- Mobile Card -->
          <div
            class="mobile-card"
            [ngClass]="{
              expanded: expanded,
              'status-active': item?.Status === 'Active',
              'status-failed': item?.Status === 'Failed',
              'status-paused': item?.Status === 'Paused',
              'status-processing': item?.Status === 'Processing',
              'status-deleted': item?.Status === 'Deleted',
              'status-queued': item?.Status === 'Queued',
            }"
          >
            <!-- Card Header: Checkbox, ID, Status Badge, Quick Icons -->
            <div class="mobile-card-header">
              <div class="mobile-card-header-left">
                <!-- Checkbox -->
                <p-checkbox
                  *ngIf="showCheckboxes"
                  [value]="item"
                  [(ngModel)]="selectedItems"
                  [binary]="false"
                  (onChange)="onSelectionChange()"
                  [disabled]="loading"
                />
              </div>

              <div class="mobile-card-header-center">
                <!-- ID Link -->
                <a
                  href="javascript:;"
                  class="mobile-card-id"
                  (click)="rowAction.emit({ action: 'edit', item })"
                >
                  #{{ item?.Id }}
                </a>
                <!-- Status Badge (moved here) -->
                <p-tag
                  [value]="item?.Status"
                  [severity]="getStatusSeverity(item?.Status)"
                />
              </div>

              <div class="mobile-card-header-right">
                <!-- Quick Access Icons: Event Logs & Cycles History -->
                <div class="mobile-quick-icons">
                  <button
                    class="mobile-icon-btn"
                    (click)="openHistoryDialog(item); $event.stopPropagation()"
                    pTooltip="Event Logs"
                    tooltipPosition="top"
                  >
                    <i class="pi pi-list"></i>
                  </button>
                  <button
                    class="mobile-icon-btn"
                    (click)="openCyclesDialog(item); $event.stopPropagation()"
                    pTooltip="Cycles History"
                    tooltipPosition="top"
                  >
                    <i class="pi pi-history"></i>
                  </button>
                </div>
              </div>
            </div>

            <!-- Card Body: Customer Info - Clickable to expand -->
            <div class="mobile-card-body" [pRowToggler]="item">
              <!-- Customer Info -->
              <div class="mobile-card-customer">
                <span class="mobile-card-customer-name">{{
                  getCustomerFullName(item)
                }}</span>
                <span class="mobile-card-customer-email">
                  {{ item?.CustomerEmail }}
                </span>
              </div>
            </div>

            <!-- Card Footer: Compact Info Display with Vertical Separators -->
            <div class="mobile-card-footer" [pRowToggler]="item">
              <div class="mobile-card-info-inline">
                <!-- Frequency -->
                <div class="mobile-card-info-item">
                  <span class="mobile-card-info-label">EVERY</span>
                  <span class="mobile-card-info-value">{{
                    formatFrequencyCompact(item?.Frequency, item?.FrequencyType)
                  }}</span>
                </div>
                <!-- Separator -->
                <span class="mobile-card-separator"></span>
                <!-- Next Date -->
                <div
                  class="mobile-card-info-item"
                  *ngIf="item?.NextOccurrenceUtc && site"
                >
                  <span class="mobile-card-info-label">NEXT</span>
                  <span class="mobile-card-info-value">{{
                    formatDateWithSiteTimezone(
                      item?.NextOccurrenceUtc,
                      site?.UtcOffset
                    ) | date: "MMM d"
                  }}</span>
                </div>
                <!-- Separator -->
                <span class="mobile-card-separator"></span>
                <!-- Items (Item 8: Changed from CYCLE to ITEMS) -->
                <div class="mobile-card-info-item">
                  <span class="mobile-card-info-label">ITEMS</span>
                  <span class="mobile-card-info-value">{{
                    formatItemsCount(item)
                  }}</span>
                </div>
                <!-- Separator -->
                <span class="mobile-card-separator"></span>
                <!-- Value - Always show, display $0 when subtotal is zero -->
                <div class="mobile-card-info-item">
                  <span class="mobile-card-info-label">VALUE</span>
                  <span class="mobile-card-info-value">{{
                    formatCurrency(item?.Subtotal ?? 0, item?.CurrencyIso)
                  }}</span>
                </div>
              </div>
            </div>
          </div>
        </td>
      </tr>
    </ng-template>

    <!-- Row Expansion Template -->
    <ng-template pTemplate="expandedrow" let-item>
      <tr>
        <td
          [attr.colspan]="
            getPrimaryColumns().length +
            (showCheckboxes ? 1 : 0) +
            (showActions ? 1 : 0) +
            1
          "
          class="expanded-row-cell"
        >
          <div class="expanded-row-content">
            <!-- Quick Actions (Mobile) - Scroll container to prevent card overflow -->
            <!-- Item 4: Use filter-style blue buttons for mobile -->
            <!-- Item 10: Show edit button in expanded row quick actions -->
            <div
              class="quick-actions-scroll-container"
              *ngIf="shouldShowQuickActions(item?.Status)"
            >
              <app-scheduled-order-quick-actions
                [order]="convertToScheduledOrder(item)"
                [displayMode]="'horizontal'"
                [showProcessNowButton]="true"
                [filterStyle]="true"
                [showEditButton]="true"
                (actionClick)="onQuickAction($event)"
              />
            </div>

            <!-- Customer Information -->
            <div class="detail-section">
              <div class="section-header">Customer Information</div>
              <dl class="detail-list">
                <div class="detail-item">
                  <dt>First Name</dt>
                  <dd>{{ item.CustomerFirstName || "-" }}</dd>
                </div>
                <div class="detail-item">
                  <dt>Last Name</dt>
                  <dd>{{ item.CustomerLastName || "-" }}</dd>
                </div>
              </dl>
            </div>

            <!-- Shipping Information -->
            <div
              class="detail-section"
              *ngIf="
                item.ShippingPostcode ||
                item.ShippingState ||
                item.ShippingCountry ||
                item.PreferredShippingRateOption
              "
            >
              <div class="section-header">Shipping Information</div>
              <dl class="detail-list">
                <div
                  class="detail-item"
                  *ngIf="
                    item.ShippingPostcode ||
                    item.ShippingState ||
                    item.ShippingCountry
                  "
                >
                  <dt>Postal Code</dt>
                  <dd>{{ item.ShippingPostcode || "-" }}</dd>
                </div>
                <div
                  class="detail-item"
                  *ngIf="item.ShippingState || item.ShippingCountry"
                >
                  <dt>State</dt>
                  <dd>{{ item.ShippingState || "-" }}</dd>
                </div>
                <div class="detail-item" *ngIf="item.ShippingCountry">
                  <dt>Country</dt>
                  <dd>{{ item.ShippingCountry }}</dd>
                </div>
                <div
                  class="detail-item"
                  *ngIf="item.PreferredShippingRateOption"
                >
                  <dt>Shipping Option</dt>
                  <dd>{{ item.PreferredShippingRateOption }}</dd>
                </div>
              </dl>
            </div>

            <!-- Payment Information -->
            <div class="detail-section">
              <div class="section-header">Payment Information</div>
              <dl class="detail-list">
                <div class="detail-item" *ngIf="item.PaymentMethodName">
                  <dt>Payment Method</dt>
                  <dd>{{ item.PaymentMethodName }}</dd>
                </div>
                <div class="detail-item">
                  <dt>Currency</dt>
                  <dd>{{ item.CurrencyIso }}</dd>
                </div>
              </dl>
            </div>

            <!-- Order Details -->
            <div class="detail-section">
              <div class="section-header">Order Details</div>
              <dl class="detail-list">
                <!-- Item 9: Changed from Product Quantity to Items -->
                <div class="detail-item">
                  <dt>Items</dt>
                  <dd>{{ formatItemsCount(item) }}</dd>
                </div>
                <div class="detail-item" *ngIf="item.Subtotal">
                  <dt>Subtotal</dt>
                  <dd>{{ formatCurrency(item.Subtotal, item.CurrencyIso) }}</dd>
                </div>
                <div class="detail-item" *ngIf="item.LifetimeValue">
                  <dt>Lifetime Value</dt>
                  <dd>
                    {{ formatCurrency(item.LifetimeValue, item.CurrencyIso) }}
                  </dd>
                </div>
              </dl>
            </div>

            <!-- Processing Information -->
            <div class="detail-section">
              <div class="section-header">Processing Information</div>
              <dl class="detail-list">
                <div class="detail-item" *ngIf="item.LastOccurrenceUtc">
                  <dt>Last Occurrence</dt>
                  <dd>
                    {{
                      formatDateWithSiteTimezone(
                        item.LastOccurrenceUtc,
                        site?.UtcOffset
                      )
                    }}
                  </dd>
                </div>
                <div class="detail-item" *ngIf="item.LastProcessingCycle">
                  <dt>Last Cycle Status</dt>
                  <dd>
                    <a
                      href="javascript:;"
                      class="link"
                      [pTooltip]="'View processing logs for this cycle'"
                      tooltipPosition="top"
                      (click)="openProcessingLogsDialog(item)"
                    >
                      {{ getLastProcessingCycleStatus(item) }}
                    </a>
                  </dd>
                </div>
                <div class="detail-item" *ngIf="item.ProcessingErrorCode">
                  <dt>Error Code</dt>
                  <dd>
                    <span class="error-code"
                      >({{ item.ProcessingErrorCode }})</span
                    >
                    <a
                      *ngIf="
                        getProcessingErrorDocumentationUrl(
                          item.ProcessingErrorCode
                        ) as docUrl
                      "
                      [href]="docUrl"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="link ml-1"
                      pTooltip="View documentation"
                      tooltipPosition="top"
                    >
                      <i class="pi pi-external-link"></i>
                    </a>
                    <div class="error-description">
                      {{
                        getProcessingErrorDescription(item.ProcessingErrorCode)
                      }}
                    </div>
                  </dd>
                </div>
                <div class="detail-item">
                  <dt>Frequency Type</dt>
                  <dd>{{ formatFrequencyType(item.FrequencyType) }}</dd>
                </div>
              </dl>
            </div>

            <!-- Additional Details -->
            <div class="detail-section" *ngIf="item.LastEditableDate">
              <div class="section-header">Additional Details</div>
              <dl class="detail-list">
                <div class="detail-item">
                  <dt>Last Editable Date</dt>
                  <dd>{{ formatDate(item.LastEditableDate) }}</dd>
                </div>
              </dl>
            </div>
          </div>
        </td>
      </tr>
    </ng-template>

    <!-- Empty State -->
    <ng-template pTemplate="emptymessage">
      <tr>
        <td
          [attr.colspan]="
            getPrimaryColumns().length +
            (showCheckboxes ? 1 : 0) +
            (showActions ? 1 : 0) +
            1
          "
          class="text-center p-4"
        >
          <div class="flex flex-column align-items-center gap-3">
            <i class="pi pi-inbox text-4xl text-400"></i>
            <span class="text-lg font-medium text-600"
              >No scheduled orders found</span
            >
            <span class="text-sm text-500"
              >Try adjusting your filters or search criteria</span
            >
          </div>
        </td>
      </tr>
    </ng-template>

    <!-- Loading State -->
    <ng-template pTemplate="loadingbody">
      <app-table-skeleton-rows [rows]="5" [columns]="skeletonColumns" />
    </ng-template>
  </p-table>

  <!-- Context Menu -->
  <p-menu
    #contextMenu
    [model]="contextMenuItems"
    [popup]="true"
    appendTo="body"
  />

  <!-- Timezone Details Popover -->
  <p-popover
    #timezoneOverlay
    appendTo="body"
    [dismissable]="true"
    styleClass="timezone-popover"
  >
    <div class="p-4 max-w-sm">
      <div class="font-semibold text-gray-900 mb-2 text-sm">
        Timezone Details
      </div>
      <div
        class="white-space-pre-line text-sm text-gray-700 leading-relaxed"
        [innerHTML]="timezoneDetails"
      ></div>
    </div>
  </p-popover>
</div>

<!-- Empty State when no data -->
<div
  *ngIf="!loading && (!data || !data.Items || data.Items.length === 0)"
  class="text-center p-4"
>
  <p class="text-gray-500">No scheduled orders found.</p>
</div>

<!-- Mobile Bottom Sheet removed - redundant with expanded row view -->
